import { POSTS_BATCH_2 } from "./posts2";
import { POSTS_BATCH_3 } from "./posts3";
import { POSTS_BATCH_4 } from "./posts4";
import { POSTS_BATCH_5 } from "./posts5";
import { POSTS_BATCH_6 } from "./posts6";
import { POSTS_BATCH_7 } from "./posts7";
import { POSTS_BATCH_8 } from "./posts8";
import { POSTS_BATCH_9 } from "./posts9";
import { POSTS_BATCH_10 } from "./posts10";

const CORE_POSTS = [
  {
    slug: "why-your-business-website-isnt-generating-enquiries",
    title: "Why Your Business Website Isn't Generating Enquiries",
    excerpt: "Your website gets visitors but no calls. Here are the seven most common reasons business websites fail to convert — and what to do about each one.",
    category: "Website Growth",
    date: "2026-06-18",
    readTime: "7 min read",
    sections: [
      {
        paragraphs: [
          "You paid for a website. It looks fine. But weeks go by and the phone doesn't ring. If this sounds familiar, you're not alone — most business websites in India generate little or no enquiry flow, and it's rarely bad luck. It's usually one of seven fixable problems.",
        ],
      },
      {
        h2: "1. There's no clear next step",
        paragraphs: [
          "A visitor lands on your homepage, reads a little, and then… nothing. No obvious button, no reason to act now, no clear path to contact you. Every page on your website needs one primary action — call, WhatsApp, or a short form — visible without scrolling.",
        ],
      },
      {
        h2: "2. It talks about you, not about them",
        paragraphs: [
          "'We were established in 2009. We are a leading provider of…' — visitors skim past this instantly. High-converting websites lead with the customer's problem and the outcome you deliver. Your history matters only after they're interested.",
        ],
      },
      {
        h2: "3. It's slower than your competitor's",
        paragraphs: [
          "On a mobile connection, every extra second of load time loses a measurable share of visitors. If your site takes five seconds to appear, many potential customers never see it at all — they hit back and call the next result on Google.",
        ],
      },
      {
        h2: "4. Contacting you takes effort",
        paragraphs: [
          "A contact page with a long form and a promise to 'get back within 48 hours' is where enquiries go to die. Business owners message on WhatsApp because it's instant. Meeting customers on the channel they already use lifts conversion dramatically.",
        ],
      },
      {
        h2: "5. Google can't find it",
        paragraphs: [
          "If your pages have generic titles, no service-specific pages and no local signals, you simply don't appear when someone searches 'your service + your city'. No traffic means no enquiries, regardless of how good the site looks.",
        ],
      },
      {
        h2: "6. There's no proof",
        paragraphs: [
          "B2B buyers de-risk decisions. Without case studies, client names, photos of real work or reviews, an enquiry feels like a gamble. Proof elements near every call-to-action meaningfully increase submissions.",
        ],
      },
      {
        h2: "7. Nothing ever changes",
        paragraphs: [
          "A website untouched since launch slowly falls behind: competitors publish content, rankings shift, offers go stale. The businesses winning online treat their website as a living sales channel that improves every month.",
        ],
      },
      {
        h2: "The fix",
        paragraphs: [
          "None of these require a massive budget — they require intent. A website rebuilt around conversion: fast, clear, WhatsApp-native, SEO-structured and continuously improving. If your current site isn't generating enquiries, that's the first thing worth fixing this quarter.",
        ],
      },
    ],
  },
  {
    slug: "how-long-does-seo-take-for-a-business-website",
    title: "How Long Does SEO Take for a Business Website?",
    excerpt: "The honest timeline for ranking a business website on Google — what happens in month 1, month 3 and month 6, and how to get results faster.",
    category: "SEO",
    date: "2026-06-25",
    readTime: "6 min read",
    sections: [
      {
        paragraphs: [
          "'How long until I'm on the first page of Google?' Every business owner asks it, and most agencies dodge it. Here's the honest answer: for a typical Indian business website targeting realistic keywords, meaningful results take three to six months. Here's what actually happens during that time.",
        ],
      },
      {
        h2: "Weeks 1–4: Foundation and indexing",
        paragraphs: [
          "Google discovers and indexes your pages. Technical foundations — sitemap, schema, speed, correct titles — determine whether the months ahead compound or stall. A site built SEO-friendly from day one skips months of retrofit work.",
        ],
      },
      {
        h2: "Months 2–3: Early movement",
        paragraphs: [
          "You'll typically see impressions grow in Search Console first, then rankings for longer, more specific searches: 'industrial packaging manufacturer Faridabad' moves before 'packaging manufacturer'. These specific searches convert better anyway — the searcher knows exactly what they want.",
        ],
      },
      {
        h2: "Months 4–6: Compounding",
        paragraphs: [
          "With consistent content — service pages, location pages, articles answering your customers' real questions — rankings broaden. This is where organic enquiries typically begin arriving without ad spend.",
        ],
      },
      {
        h2: "What changes the timeline",
        list: [
          "Competition: 'dentist in South Delhi' is harder than 'dentist in Dehradun'",
          "Starting point: an established domain with history moves faster than a brand-new one",
          "Consistency: two articles a month compounds; one article every six months doesn't",
          "Technical health: slow, broken sites get held back regardless of content",
        ],
      },
      {
        h2: "How to get results faster",
        paragraphs: [
          "Target specific, commercial searches first rather than broad terms. Build location pages for the exact cities you serve. Publish content that answers the questions your customers ask before buying. And keep your Google Business Profile active — local results often move within weeks.",
        ],
      },
      {
        h2: "The compounding advantage",
        paragraphs: [
          "SEO feels slow until it doesn't. Unlike ads — which stop the day you stop paying — every page and article you publish keeps working. A business that starts now will, a year from today, have an enquiry channel its competitors can't buy their way past.",
        ],
      },
    ],
  },
  {
    slug: "why-whatsapp-can-improve-website-conversion",
    title: "Why WhatsApp Can Improve Your Website Conversion Rate",
    excerpt: "Forms get ignored. WhatsApp gets answered. Why putting WhatsApp at the centre of your website can multiply enquiries from the same traffic.",
    category: "Lead Generation",
    date: "2026-07-02",
    readTime: "5 min read",
    sections: [
      {
        paragraphs: [
          "Here's a simple test: when did you last fill in a 'Contact Us' form on a business website? Now — when did you last WhatsApp a business? Your customers behave exactly like you do. In India, WhatsApp isn't a messaging app; it's how business gets done. Websites that accept this convert measurably better.",
        ],
      },
      {
        h2: "Forms have friction, WhatsApp has none",
        paragraphs: [
          "A form asks for effort and offers uncertainty: fill five fields, submit, hope someone replies. WhatsApp offers the opposite — one tap, and the visitor is in a conversation thread with your business, in an app they check fifty times a day.",
        ],
      },
      {
        h2: "Speed wins the deal",
        paragraphs: [
          "B2B buyers typically message two or three suppliers at once. The first useful reply usually gets the business. WhatsApp puts the enquiry in your pocket instantly — you can respond in minutes while competitors' form submissions sit in an inbox until Monday.",
        ],
      },
      {
        h2: "The conversation continues",
        paragraphs: [
          "A form submission is a dead end. A WhatsApp conversation is a thread you can return to — send a brochure tomorrow, a price list next week, a follow-up next month. Every enquiry becomes a contact, not a ticket.",
        ],
      },
      {
        h2: "How to use it properly",
        list: [
          "Prefilled messages: each button opens WhatsApp with context — which page, which service — so you know what they want before they type",
          "Visible but not desperate: a floating button, plus inline CTAs after key sections",
          "Forms and WhatsApp together: forms capture structured detail, the WhatsApp handoff makes it instant",
          "Business hours honesty: an auto-reply with expected response time keeps leads warm",
        ],
      },
      {
        h2: "The numbers argument",
        paragraphs: [
          "You don't need more traffic to get more enquiries — you need more of your existing visitors to act. Moving your primary call-to-action to WhatsApp is often the single highest-leverage change a business website can make, and it costs nothing but a redesign decision.",
        ],
      },
    ],
  },
];

// Publication dates are the days each batch of articles went live on the site
// (from the repository history). The articles' original drafting dates were
// earlier than the site itself, so they are not shown.
const PUBLISHED = [
  [CORE_POSTS, "2026-09-03"],
  [[...POSTS_BATCH_2, ...POSTS_BATCH_3, ...POSTS_BATCH_4, ...POSTS_BATCH_5, ...POSTS_BATCH_6, ...POSTS_BATCH_7, ...POSTS_BATCH_8], "2026-09-07"],
  [[...POSTS_BATCH_9, ...POSTS_BATCH_10], "2026-09-18"],
];
const UPDATED = "2026-10-15"; // last site-wide content revision

const words = (post) =>
  (post.sections || []).reduce(
    (n, s) => n + [s.h2, ...(s.paragraphs || []), ...(s.list || [])].filter(Boolean).join(" ").split(/\s+/).length,
    0
  );

export const POSTS = PUBLISHED.flatMap(([batch, date]) =>
  batch.map((p, i) => ({
    ...p,
    date,
    updated: UPDATED,
    order: i,
    // Reading time from the real word count (about 220 words a minute).
    readTime: `${Math.max(2, Math.round(words(p) / 220))} min read`,
  }))
).sort((a, b) => new Date(b.date) - new Date(a.date) || a.order - b.order);

export const BLOG_CATEGORIES = [
  "All",
  "Website Growth",
  "SEO",
  "Lead Generation",
  "B2B Marketing",
  "Website Design",
  "Local SEO",
  "Business Growth",
];
